import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MessageCircle, Mail, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";

gsap.registerPlugin(ScrollTrigger);

export const Contact = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(headerRef.current, {
        x: -100,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger: headerRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse"
        }
      });

      cardsRef.current.forEach((card, index) => {
        gsap.from(card, {
          y: 50,
          opacity: 0,
          scale: 0.95,
          duration: 0.6,
          ease: "power3.out",
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none none reverse"
          },
          delay: index * 0.15
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-background border-t border-border/50"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left Column - Header */}
          <div ref={headerRef}>
            <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-medium block mb-4">
              06
            </span>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              Let's Work Together
            </h2>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              If you are an entrepreneur, investor or company looking to develop new opportunities, 
              feel free to get in touch. I'm available via WhatsApp or Email.
            </p>
          </div>

          {/* Right Column - Contact Options */}
          <div className="space-y-6">
            {/* WhatsApp Card */}
            <a
              href="https://wa.me/62822504050"
              target="_blank"
              rel="noopener noreferrer"
              ref={(el) => cardsRef.current[0] = el}
              data-testid="whatsapp-contact"
              className="card-3d group block p-8 border border-border/50 hover:bg-[#25D366]/5 hover:border-[#25D366]/30 transition-all duration-300"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#25D366]/10 flex items-center justify-center group-hover:bg-[#25D366]/20 transition-colors">
                    <MessageCircle className="w-6 h-6 text-[#25D366]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-foreground mb-1">
                      WhatsApp
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Fastest response • Usually within hours
                    </p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-[#25D366] group-hover:translate-x-1 transition-all" />
              </div>
              <div className="mt-4 pt-4 border-t border-border/30">
                <span className="text-sm text-muted-foreground">+62 822 504 050</span>
              </div>
            </a>

            {/* Email Card */}
            <a
              href="mailto:j@ltvt.fr?subject=Project%20Inquiry&body=Hello%20Jérémie,%0A%0AI%20would%20like%20to%20discuss%20a%20project%20with%20you.%0A%0ABest%20regards"
              ref={(el) => cardsRef.current[1] = el}
              data-testid="email-contact"
              className="card-3d group block p-8 border border-border/50 hover:bg-foreground/5 hover:border-foreground/30 transition-all duration-300"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-foreground/5 flex items-center justify-center group-hover:bg-foreground/10 transition-colors">
                    <Mail className="w-6 h-6 text-foreground" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-foreground mb-1">
                      Email
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      For detailed inquiries • Response within 24h
                    </p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground group-hover:translate-x-1 transition-all" />
              </div>
              <div className="mt-4 pt-4 border-t border-border/30">
                <span className="text-sm text-muted-foreground">j@ltvt.fr</span>
              </div>
            </a>

            {/* Quick Message CTA */}
            <div 
              ref={(el) => cardsRef.current[2] = el}
              className="pt-6 text-center"
            >
              <p className="text-sm text-muted-foreground mb-4">
                Prefer a quick chat?
              </p>
              <Button
                onClick={() => window.open('https://wa.me/62822504050?text=Hello%20Jérémie,%20I%20found%20your%20website%20and%20would%20like%20to%20discuss%20a%20project.', '_blank')}
                className="group bg-[#25D366] hover:bg-[#20bd5a] text-white px-8 py-6 text-sm uppercase tracking-widest font-medium"
              >
                <MessageCircle className="mr-2 w-4 h-4" />
                Start a Conversation
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
